/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, Configuration, PopupRequest } from '@azure/msal-browser';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1_SignIn',
        forgotPassword: 'b2c_1_reset',
        editProfile: 'b2c_1_edit_profile',
    },
    authorities: {
        signUpSignIn: {
            authority: process.env.REACT_APP_AZURE_AUTHORITY_SIGNUPSIGNIN,
        },
        forgotPassword: {
            authority: process.env.REACT_APP_AZURE_AUTHORITY_FORGOT_PASSWORD,
        },
        editProfile: {
            authority: process.env.REACT_APP_AZURE_AUTHORITY_EDIT_PROFILE,
        },
    },
    authorityDomain: process.env.REACT_APP_AZURE_AUTHORITY_DOMAIN,
};

/**
 * Configuration object to be passed to MSAL instance on creation
 * For a full list of MSAL.js configuration parameters, visit:
 */
export const msalConfig: Configuration = {
    auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID ?? '', // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain ?? ''], // Mark your B2C tenant's domain as trusted.
        redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If 'true', will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. 'sessionStorage' is more secure, but 'localStorage' gives you SSO between tabs.
        storeAuthStateInCookie: true, // Set this to 'true' if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (process.env.NODE_ENV === 'production') {
                    return;
                }
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiHello: {
        scopes: [process.env.REACT_APP_AZURE_SCOPE_1 ?? ''], // e.g. api://xxxxxx/access_as_user
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request
 * For more information about OIDC scopes, visit:
 */
export const loginRequest: PopupRequest = {
    scopes: [...protectedResources.apiHello.scopes],
};
